/**
 * Application orchestrator.
 *
 * Owns the lifecycle of every subsystem and is the only place where they meet. Each
 * subsystem stays ignorant of the others: the camera does not know about audio, the audio
 * engine does not know about hands, the scene does not know about MediaPipe.
 *
 * Two independent clocks:
 *  - a `requestAnimationFrame` render loop at display refresh rate, and
 *  - hand inference, scheduled by `HandTracker` at 10-30 Hz on a worker where possible.
 * Control values are updated from the latest available inference result on every rendered
 * frame, so the visuals and audio stay smooth even when inference is slower.
 */

import { AppStateMachine, STATE_COPY } from './AppStateMachine';
import type { AppErrorInfo, AppState } from './AppStateMachine';
import { DEFAULT_PLACEMENT, SettingsStore } from './settings';
import type { Settings } from './settings';
import { CameraController, CameraError, isCameraSupported } from '../camera/CameraController';
import type { CameraStatus } from '../camera/CameraController';
import { VideoCoordinateMapper } from '../camera/videoCoordinates';
import { HandTracker } from '../tracking/HandTracker';
import type { InferenceMode } from '../tracking/HandTracker';
import { HandIdentityTracker } from '../tracking/HandIdentityTracker';
import { configFromSmoothingAmount } from '../tracking/LandmarkSmoother';
import type { HandLandmarkSample, InferenceResult, Point2, TrackedHand } from '../tracking/types';
import { clamp01 } from '../tracking/types';
import type { ThereminScene } from '../scene/ThereminScene';
import type { WebXRPlacement } from '../xr/WebXRPlacement';
import type { HandVisual } from '../scene/InteractionZones';
import type { OverlayHand } from '../scene/LandmarkOverlay';
import type { PlacementGestureController } from '../scene/SpatialPlacement';
import { ThereminAudioEngine } from '../audio/ThereminAudioEngine';
import { ThereminController } from '../interaction/ThereminController';
import type { ControlOutput } from '../interaction/ThereminController';
import { CalibrationController } from '../interaction/CalibrationController';
import { PerformanceMonitor } from '../diagnostics/PerformanceMonitor';
import { createUiShell } from '../ui/UiShell';
import type {
  CalibrationViewModel,
  CapabilityTier,
  DiagnosticsSnapshot,
  HandTag,
  LiveReadout,
  UiCallbacks,
  UiCapabilities,
  UiShell,
} from '../ui/uiTypes';
import { installTestHooks } from './testHooks';
import type { TestHookHost } from './testHooks';

const FRAME_TIME_CAP_SECONDS = 0.1;

export interface AppOptions {
  container: HTMLElement;
  /** Skip MediaPipe entirely and accept injected landmark frames (used by e2e tests). */
  mockTracking?: boolean;
}

export class App implements TestHookHost {
  private readonly settings: SettingsStore;

  private readonly machine = new AppStateMachine();

  private readonly ui: UiShell;

  private readonly mapper = new VideoCoordinateMapper();

  private readonly identity = new HandIdentityTracker();

  private readonly controller = new ThereminController();

  private readonly audio = new ThereminAudioEngine();

  private readonly perf = new PerformanceMonitor();

  private readonly calibration: CalibrationController;

  private camera: CameraController | null = null;

  private tracker: HandTracker | null = null;

  private scene: ThereminScene | null = null;

  private gestures: PlacementGestureController | null = null;

  private xr: WebXRPlacement | null = null;

  private xrAvailable = false;

  private readonly mockTracking: boolean;

  private rafHandle: number | null = null;

  private lastFrameMs = 0;

  private resizeObserver: ResizeObserver | null = null;

  private disposed = false;

  private hasPlacedOnce = false;

  private trackedHands: readonly TrackedHand[] = [];

  private inferenceMode: InferenceMode = 'idle';

  // Reused per-frame buffers.
  private readonly antennaScreen = { pitch: { x: 0, y: 0 }, volume: { x: 0, y: 0 } };

  private readonly handVisuals: HandVisual[] = [];

  private readonly overlayHands: OverlayHand[] = [];

  private readonly overlayPool: OverlayHand[] = [
    { points: [], role: 'none', visible: false },
    { points: [], role: 'none', visible: false },
  ];

  private readonly handTags: HandTag[] = [];

  private readonly handTagPool: HandTag[] = [
    { role: 'pitch', u: 0.5, v: 0.5 },
    { role: 'volume', u: 0.5, v: 0.5 },
  ];

  private readonly readout: LiveReadout = {
    frequency: 0,
    note: '—',
    cents: 0,
    volume: 0,
    pitchNormalized: 0,
    volumeNormalized: 0,
    pitchHandPresent: false,
    volumeHandPresent: false,
    handCount: 0,
    sounding: false,
    vibrato: 0,
    vibratoFollowsHand: false,
  };

  private readonly diagnostics: DiagnosticsSnapshot = {
    renderFps: 0,
    inferenceFps: 0,
    inferenceMs: 0,
    cameraWidth: 0,
    cameraHeight: 0,
    handCount: 0,
    tier: 'camera-overlay',
    inferenceMode: 'idle',
    targetInferenceHz: 0,
    audioLatencyMs: 0,
    droppedFrames: 0,
  };

  private readonly capabilities: UiCapabilities = {
    canSwitchCamera: false,
    xrAvailable: false,
    xrActive: false,
    audioRunning: false,
    modelReady: false,
  };

  constructor(options: AppOptions) {
    this.settings = new SettingsStore();
    this.mockTracking =
      options.mockTracking ?? new URLSearchParams(globalThis.location?.search ?? '').has('mock');

    this.applySystemPreferences();

    this.calibration = new CalibrationController({
      fallback: {
        pitchRange: this.settings.current.pitchRange,
        volumeRange: this.settings.current.volumeRange,
      },
    });

    this.ui = createUiShell(options.container, this.buildCallbacks());
    this.machine.subscribe(({ to, context }) => {
      this.ui.setState(to, context.error);
      this.onStateChanged(to);
    });
    this.settings.subscribe((settings, changed) => this.onSettingsChanged(settings, changed));

    this.ui.setState(this.machine.current, null);
    this.ui.setSettings(this.settings.current);
    this.ui.setCapabilities(this.capabilities);
    this.syncMirroring();

    installTestHooks(this);
    this.installGlobalListeners();
    void this.detectXr();
  }

  // ---------------------------------------------------------------- lifecycle

  /**
   * Entry point after construction.
   *
   * The renderer and the Three.js bundle are loaded asynchronously so the intro screen — the
   * only thing visible before the user opts in to the camera — paints without waiting on
   * ~130 kB of gzipped renderer code.
   */
  start(): void {
    void this.initScene();
  }

  private async initScene(): Promise<void> {
    if (this.scene || this.disposed) return;
    try {
      const [{ ThereminScene, SceneUnavailableError }, { PlacementGestureController }] =
        await Promise.all([import('../scene/ThereminScene'), import('../scene/SpatialPlacement')]);
      if (this.disposed) return;

      try {
        this.scene = new ThereminScene({
          canvas: this.ui.surfaces.canvas,
          reducedMotion: this.settings.current.reducedMotion,
        });
      } catch (error) {
        this.machine.fail({
          kind: 'webgl-unavailable',
          message:
            error instanceof SceneUnavailableError
              ? error.message
              : 'WebGL is unavailable, so the instrument cannot be drawn.',
          recoverable: false,
          cause: error,
        });
        return;
      }

      const settings = this.settings.current;
      this.scene.setPlacement(settings.placement);
      this.scene.setRanges(settings.pitchRange, settings.volumeRange);
      this.scene.setOverlayEnabled(settings.showSkeleton);

      this.gestures = new PlacementGestureController(this.ui.surfaces.stage, {
        getPlacement: () => this.settings.current.placement,
        onChange: (next) => this.settings.update({ placement: next }),
        pixelsToScreen: (px, py, out) => {
          const height = this.mapper.transform.displayHeight || 1;
          out.x = px / height;
          out.y = -py / height;
          return out;
        },
      });
      // The user may already have reached placement while the renderer was loading.
      if (this.machine.current === 'placement') this.gestures.enable();

      this.observeResize();
      this.handleResize();
      this.startRenderLoop();
    } catch (error) {
      this.machine.fail({
        kind: 'webgl-unavailable',
        message: 'The renderer could not be loaded. Check your connection and try again.',
        recoverable: true,
        cause: error,
      });
    }
  }

  private applySystemPreferences(): void {
    if (typeof globalThis.matchMedia !== 'function') return;
    const query = globalThis.matchMedia('(prefers-reduced-motion: reduce)');
    if (query.matches && !this.settings.current.reducedMotion) {
      this.settings.update({ reducedMotion: true });
    }
    const onChange = (event: MediaQueryListEvent): void => {
      this.settings.update({ reducedMotion: event.matches });
    };
    query.addEventListener('change', onChange);
    this.mediaQueryCleanup = () => query.removeEventListener('change', onChange);
  }

  private mediaQueryCleanup: (() => void) | null = null;

  private buildCallbacks(): UiCallbacks {
    return {
      onRequestCamera: () => void this.acquireCamera(),
      onRetry: () => void this.retry(),
      onStartInstrument: () => void this.startInstrument(),
      onStopInstrument: () => void this.stopInstrument(),
      onToggleMute: () => this.settings.update({ muted: !this.settings.current.muted }),
      onSwitchCamera: () => void this.switchCamera(),
      onEnterPlacement: () => this.enterPlacement(),
      onConfirmPlacement: () => this.confirmPlacement(),
      onResetPlacement: () => this.settings.update({ placement: { ...DEFAULT_PLACEMENT } }),
      onStartCalibration: () => this.startCalibration(),
      onCalibrationAdvance: () => this.advanceCalibration(),
      onCalibrationCancel: () => this.cancelCalibration(),
      onSwapHands: () => this.settings.update({ swapHands: !this.settings.current.swapHands }),
      onResetAll: () => this.resetEverything(),
      onToggleSettings: () => undefined,
      onToggleDiagnostics: () =>
        this.settings.update({ showDiagnostics: !this.settings.current.showDiagnostics }),
      onEnterXr: () => void this.enterXr(),
      onExitXr: () => void this.exitXr(),
      onSettingChange: (key, value) => this.settings.update({ [key]: value }),
      onSettingsPatch: (patch) => this.settings.update(patch),
      onPlacementChange: (patch) =>
        this.settings.update({ placement: { ...this.settings.current.placement, ...patch } }),
    };
  }

  // ------------------------------------------------------------------ camera

  private async acquireCamera(): Promise<void> {
    if (!isCameraSupported()) {
      this.machine.fail({
        kind: 'camera-unavailable',
        message: 'This browser does not expose a camera API (getUserMedia is missing).',
        recoverable: false,
      });
      return;
    }
    this.machine.transition('requestingCamera');

    if (!this.camera) {
      this.camera = new CameraController(this.ui.surfaces.video, {
        onInterrupted: (info) => this.machine.fail(info),
        onSettingsChanged: (status) => this.onCameraStatus(status),
      });
    }

    try {
      const status = await this.camera.start({
        facingMode: this.settings.current.facingMode,
        height: this.settings.current.captureHeight,
      });
      this.onCameraStatus(status);
    } catch (error) {
      this.machine.fail(
        error instanceof CameraError
          ? error.info
          : {
              kind: 'camera-unavailable',
              message: error instanceof Error ? error.message : String(error),
              recoverable: true,
            },
      );
      return;
    }

    await this.initializeTracking();
  }

  private onCameraStatus(status: CameraStatus): void {
    this.diagnostics.cameraWidth = status.width;
    this.diagnostics.cameraHeight = status.height;
    if (status.facingMode !== this.settings.current.facingMode) {
      this.settings.update({ facingMode: status.facingMode });
    }
    this.capabilities.canSwitchCamera = this.camera?.canSwitch ?? false;
    this.ui.setCapabilities(this.capabilities);
    this.mapper.update({ frameWidth: status.width, frameHeight: status.height });
    this.syncMirroring();
    this.handleResize();
  }

  private async switchCamera(): Promise<void> {
    const camera = this.camera;
    if (!camera) return;
    try {
      const status = await camera.switchCamera(this.settings.current.captureHeight);
      this.settings.update({ facingMode: status.facingMode });
      this.onCameraStatus(status);
      // Identity and role assignment are meaningless across a camera flip.
      this.identity.reset();
      this.controller.reset();
      this.tracker?.attach(this.ui.surfaces.video, camera.videoTrack);
      this.ui.notify(`Switched to the ${status.facingMode === 'user' ? 'front' : 'rear'} camera`);
    } catch (error) {
      this.ui.notify(
        error instanceof CameraError ? error.info.message : 'Could not switch camera',
        'warn',
      );
    }
  }

  private syncMirroring(): void {
    const settings = this.settings.current;
    // Rear cameras are never mirrored; the preference only applies to user-facing capture.
    const mirrored = settings.mirrorPreview && settings.facingMode === 'user';
    this.mapper.update({ mirrored });
    this.ui.surfaces.video.classList.toggle('is-mirrored', mirrored);
  }

  // ---------------------------------------------------------------- tracking

  private async initializeTracking(): Promise<void> {
    this.machine.transition('loadingModel');

    if (this.mockTracking) {
      this.capabilities.modelReady = true;
      this.inferenceMode = 'main-thread';
      this.ui.setCapabilities(this.capabilities);
      this.finishAcquisition();
      return;
    }

    if (!this.tracker) {
      this.tracker = new HandTracker(
        {
          onResult: (result) => this.onInference(result),
          onError: (info) => this.onTrackingError(info),
          onModeChange: (mode) => {
            this.inferenceMode = mode;
            this.diagnostics.inferenceMode = mode;
          },
        },
        { preferWorker: true },
      );
    }

    try {
      await this.tracker.initialize();
    } catch (error) {
      this.machine.fail({
        kind: 'model-download',
        message: error instanceof Error ? error.message : String(error),
        recoverable: true,
        cause: error,
      });
      return;
    }

    this.capabilities.modelReady = true;
    this.ui.setCapabilities(this.capabilities);
    this.tracker.attach(this.ui.surfaces.video, this.camera?.videoTrack ?? null);
    this.applySmoothing(this.settings.current.smoothing);
    this.finishAcquisition();
  }

  private finishAcquisition(): void {
    this.machine.setInstrumentArmed(true);
    if (!this.hasPlacedOnce) {
      this.hasPlacedOnce = true;
      this.machine.transition('placement');
      this.gestures?.enable();
      return;
    }
    this.machine.transition('ready');
  }

  private onTrackingError(info: AppErrorInfo): void {
    // A single bad frame should not take the instrument down; only setup failures do.
    if (info.kind === 'model-download' || info.kind === 'wasm-init') {
      this.machine.fail(info);
      return;
    }
    this.ui.notify(info.message, 'warn');
  }

  private onInference(result: InferenceResult): void {
    this.perf.onInference(result.timestamp, result.inferenceMs);
    if (result.frameWidth > 0 && result.frameHeight > 0) {
      this.mapper.update({ frameWidth: result.frameWidth, frameHeight: result.frameHeight });
      this.diagnostics.cameraWidth = result.frameWidth;
      this.diagnostics.cameraHeight = result.frameHeight;
    }
    this.trackedHands = this.identity.update(result.hands, this.mapper, result.timestamp);
  }

  /** Test hook: feed landmark samples as if they came from MediaPipe. */
  injectInference(hands: HandLandmarkSample[], frameWidth: number, frameHeight: number): void {
    this.onInference({
      hands,
      timestamp: performance.now(),
      inferenceMs: 1,
      frameWidth,
      frameHeight,
    });
  }

  private applySmoothing(amount: number): void {
    this.identity.setSmoothing(configFromSmoothingAmount(amount));
  }

  // ------------------------------------------------------------------- audio

  private async startInstrument(): Promise<void> {
    try {
      await this.audio.start();
      this.audio.setMasterVolume(this.settings.current.masterVolume);
      this.audio.setMuted(this.settings.current.muted);
      this.audio.setVibratoShape(
        this.settings.current.vibratoRate,
        this.settings.current.vibratoDepth,
      );
      this.capabilities.audioRunning = this.audio.running;
      this.ui.setCapabilities(this.capabilities);
      if (this.machine.current === 'ready' || this.machine.current === 'noHands') {
        this.machine.transition('playing');
      }
    } catch (error) {
      this.machine.transition('error', {
        kind: 'audio',
        message: error instanceof Error ? error.message : 'Audio could not be started.',
        recoverable: true,
        cause: error,
      });
    }
  }

  private async stopInstrument(): Promise<void> {
    await this.audio.suspend();
    this.capabilities.audioRunning = false;
    this.ui.setCapabilities(this.capabilities);
    if (this.machine.current === 'playing') this.machine.transition('ready');
  }

  // --------------------------------------------------------------- placement

  private enterPlacement(): void {
    if (!this.machine.transition('placement')) return;
    this.gestures?.enable();
  }

  private confirmPlacement(): void {
    this.gestures?.disable();
    this.settings.flush();
    this.machine.transition(this.audio.running ? 'playing' : 'ready');
    if (!this.audio.running) {
      // First thing after placement is always the same question — "now what?" — so answer it
      // before it is asked.
      this.ui.notify('Press Start, then raise both hands into view');
    }
  }

  // ------------------------------------------------------------- calibration

  private startCalibration(): void {
    if (!this.machine.transition('calibrating')) return;
    this.calibration.setFallback({
      pitchRange: this.settings.current.pitchRange,
      volumeRange: this.settings.current.volumeRange,
    });
    this.calibration.start();
    this.pushCalibrationModel();
  }

  private advanceCalibration(): void {
    const result = this.calibration.advance();
    if (result) {
      this.settings.update({
        pitchRange: result.pitchRange,
        volumeRange: result.volumeRange,
        calibrated: true,
      });
      this.ui.setCalibration(null);
      this.ui.notify('Calibration saved');
      this.machine.transition(this.audio.running ? 'playing' : 'ready');
      return;
    }
    this.pushCalibrationModel();
  }

  private cancelCalibration(): void {
    this.calibration.cancel();
    this.ui.setCalibration(null);
    this.machine.transition(this.audio.running ? 'playing' : 'ready');
  }

  private pushCalibrationModel(): void {
    const snapshot = this.calibration.snapshot;
    const model: CalibrationViewModel = {
      step: snapshot.step.id,
      index: snapshot.index,
      total: snapshot.total,
      title: snapshot.step.title,
      instruction: snapshot.step.instruction,
      progress: snapshot.progress,
      samples: snapshot.samples,
      canAdvance: snapshot.canAdvance,
    };
    this.ui.setCalibration(model);
  }

  // ---------------------------------------------------------------- settings

  private onSettingsChanged(
    settings: Readonly<Settings>,
    changed: ReadonlySet<keyof Settings>,
  ): void {
    this.ui.setSettings(settings);

    if (changed.has('smoothing')) this.applySmoothing(settings.smoothing);
    if (changed.has('placement')) this.scene?.setPlacement(settings.placement);
    if (changed.has('pitchRange') || changed.has('volumeRange')) {
      this.scene?.setRanges(settings.pitchRange, settings.volumeRange);
    }
    if (changed.has('showSkeleton')) this.scene?.setOverlayEnabled(settings.showSkeleton);
    if (changed.has('reducedMotion')) this.scene?.setReducedMotion(settings.reducedMotion);
    if (changed.has('mirrorPreview') || changed.has('facingMode')) this.syncMirroring();
    if (changed.has('masterVolume')) this.audio.setMasterVolume(settings.masterVolume);
    if (changed.has('muted')) this.audio.setMuted(settings.muted);
    if (changed.has('vibratoRate') || changed.has('vibratoDepth')) {
      this.audio.setVibratoShape(settings.vibratoRate, settings.vibratoDepth);
    }
    if (changed.has('captureHeight') && this.camera?.status.active) {
      void this.restartCamera();
    }
  }

  private async restartCamera(): Promise<void> {
    const camera = this.camera;
    if (!camera) return;
    try {
      const status = await camera.start({
        facingMode: this.settings.current.facingMode,
        height: this.settings.current.captureHeight,
      });
      this.onCameraStatus(status);
      this.tracker?.attach(this.ui.surfaces.video, camera.videoTrack);
    } catch (error) {
      this.ui.notify(
        error instanceof CameraError ? error.info.message : 'Could not change capture resolution',
        'warn',
      );
    }
  }

  private resetEverything(): void {
    this.settings.reset();
    this.identity.reset();
    this.controller.reset();
    this.calibration.cancel();
    this.scene?.setPlacement(this.settings.current.placement);
    this.scene?.setRanges(this.settings.current.pitchRange, this.settings.current.volumeRange);
    this.ui.setCalibration(null);
    this.ui.notify('Settings reset to defaults');
  }

  private async retry(): Promise<void> {
    const error = this.machine.context.error;
    if (error && (error.kind === 'model-download' || error.kind === 'wasm-init')) {
      this.machine.transition('loadingModel');
      await this.initializeTracking();
      return;
    }
    this.machine.transition('intro');
    await this.acquireCamera();
  }

  // --------------------------------------------------------------------- XR

  private async detectXr(): Promise<void> {
    try {
      const { detectXrCapabilities, isSpatialArViable } = await import('../xr/WebXRCapabilities');
      const caps = await detectXrCapabilities();
      this.xrAvailable = isSpatialArViable(caps);
      this.capabilities.xrAvailable = this.xrAvailable;
      this.ui.setCapabilities(this.capabilities);
    } catch {
      this.xrAvailable = false;
    }
  }

  private async enterXr(): Promise<void> {
    const scene = this.scene;
    if (!scene || !this.xrAvailable) return;
    try {
      const [{ WebXRPlacement }, { detectXrCapabilities }] = await Promise.all([
        import('../xr/WebXRPlacement'),
        import('../xr/WebXRCapabilities'),
      ]);
      const caps = await detectXrCapabilities();
      this.xr = new WebXRPlacement({
        scene,
        capabilities: caps,
        domOverlayRoot: this.ui.surfaces.root,
        events: {
          onPhase: (phase) => {
            if (phase === 'scanning') this.ui.notify('Point at a flat surface, then tap to place');
            if (phase === 'placed') this.ui.notify('Theremin placed');
          },
          onError: (info) => this.ui.notify(info.message, 'warn'),
          onSessionEnd: () => {
            this.capabilities.xrActive = false;
            this.diagnostics.tier = 'camera-overlay';
            this.ui.setCapabilities(this.capabilities);
            this.startRenderLoop();
          },
        },
      });
      this.stopRenderLoop();
      const started = await this.xr.start();
      if (!started) {
        this.startRenderLoop();
        return;
      }
      this.capabilities.xrActive = true;
      this.diagnostics.tier = 'spatial-ar';
      this.ui.setCapabilities(this.capabilities);
    } catch (error) {
      this.startRenderLoop();
      this.ui.notify(
        error instanceof Error ? error.message : 'Immersive AR could not be started',
        'warn',
      );
    }
  }

  private async exitXr(): Promise<void> {
    await this.xr?.end();
    this.xr = null;
  }

  get tier(): CapabilityTier {
    return this.capabilities.xrActive ? 'spatial-ar' : 'camera-overlay';
  }

  // ------------------------------------------------------------- render loop

  private startRenderLoop(): void {
    if (this.rafHandle !== null || this.disposed) return;
    this.lastFrameMs = performance.now();
    const tick = (now: number): void => {
      this.rafHandle = requestAnimationFrame(tick);
      this.frame(now);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopRenderLoop(): void {
    if (this.rafHandle === null) return;
    cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
  }

  private frame(now: number): void {
    const dt = Math.min(FRAME_TIME_CAP_SECONDS, Math.max(0, (now - this.lastFrameMs) / 1000));
    this.lastFrameMs = now;
    this.perf.onRender(now);
    this.perf.decay(now);

    const scene = this.scene;
    if (!scene) return;

    scene.getAntennaScreenPositions(this.antennaScreen);
    this.controller.setAnchors(this.antennaScreen.pitch, this.antennaScreen.volume);

    const settings = this.settings.current;
    const control = this.controller.update(this.trackedHands, settings, now);

    this.driveAudio(control);
    this.updateStateFromHands(control.handCount);
    this.updateSceneVisuals(scene, settings, control.pitchNormalized, control.volumeNormalized);
    this.updateReadout(control);
    this.updateDiagnostics();
    if (control.rolesReanchored) {
      // The safety net needs the best part of a second of evidence before it fires, so this
      // is rare enough to be worth saying out loud: otherwise the roles appear to move on
      // their own and the player has no idea why.
      this.ui.notify('Hands re-matched to the antennas');
    }

    if (this.machine.current === 'calibrating') {
      this.calibration.sample(control.pitchDistance, control.volumeDistance);
      this.pushCalibrationModel();
    }

    scene.render(dt);
  }

  private driveAudio(control: Readonly<ControlOutput>): void {
    if (!this.audio.running) return;
    this.audio.setFrequency(control.frequency);
    this.audio.setGain(control.gain);
    this.audio.setVibratoAmount(control.vibratoAmount);
  }

  private updateStateFromHands(handCount: number): void {
    const state = this.machine.current;
    if (state !== 'ready' && state !== 'playing' && state !== 'noHands') return;
    if (handCount === 0) {
      if (state !== 'noHands') this.machine.transition('noHands');
      return;
    }
    const target: AppState = this.audio.running ? 'playing' : 'ready';
    if (state !== target) this.machine.transition(target);
  }

  private updateSceneVisuals(
    scene: ThereminScene,
    settings: Readonly<Settings>,
    pitchAmount: number,
    volumeAmount: number,
  ): void {
    const visuals = this.handVisuals;
    visuals.length = 0;
    const pitchPoint = this.controller.controlPointFor('pitch', this.trackedHands, settings);
    const volumePoint = this.controller.controlPointFor('volume', this.trackedHands, settings);
    if (pitchPoint) visuals.push(this.handVisual(0, 'pitch', pitchPoint, pitchAmount));
    if (volumePoint) visuals.push(this.handVisual(1, 'volume', volumePoint, volumeAmount));
    scene.updateHands(visuals);
    scene.setGlow(pitchAmount, volumeAmount);

    this.updateHandTags(pitchPoint, volumePoint);

    if (!settings.showSkeleton) return;
    const overlay = this.overlayHands;
    overlay.length = 0;
    const pitchId = this.controller.state.pitchHandId;
    const volumeId = this.controller.state.volumeHandId;
    for (let i = 0; i < this.trackedHands.length && i < this.overlayPool.length; i += 1) {
      const hand = this.trackedHands[i];
      const entry = this.overlayPool[i];
      if (!hand || !entry || hand.landmarks.length === 0) continue;
      entry.points = hand.landmarks;
      entry.role = hand.id === pitchId ? 'pitch' : hand.id === volumeId ? 'volume' : 'none';
      entry.visible = hand.visible;
      overlay.push(entry);
    }
    scene.updateOverlay(overlay);
  }

  /**
   * Pin a "Pitch" / "Volume" label to each hand that is driving a control.
   *
   * This is the whole answer to "which hand is doing what": rather than asking the player to
   * infer it from the colours, the instrument says so, on the hand, while they play. If it
   * has guessed wrong, the mistake is visible immediately and Swap hands fixes it.
   */
  private updateHandTags(pitchPoint: Point2 | null, volumePoint: Point2 | null): void {
    const tags = this.handTags;
    tags.length = 0;
    const aspect = this.mapper.transform.aspect || 1;
    if (pitchPoint) tags.push(this.handTag(0, 'pitch', pitchPoint, aspect));
    if (volumePoint) tags.push(this.handTag(1, 'volume', volumePoint, aspect));
    this.ui.setHandTags(tags);
  }

  private handTag(slot: number, role: 'pitch' | 'volume', point: Point2, aspect: number): HandTag {
    const tag = this.handTagPool[slot] ?? this.handTagPool[0]!;
    tag.role = role;
    // Screen space (origin centre, +y up, one unit = stage height) -> stage fractions.
    tag.u = clamp01(point.x / aspect + 0.5);
    tag.v = clamp01(0.5 - point.y);
    return tag;
  }

  private readonly handVisualPool: HandVisual[] = [
    { id: 'pitch', role: 'pitch', x: 0, y: 0, visible: false, amount: 0 },
    { id: 'volume', role: 'volume', x: 0, y: 0, visible: false, amount: 0 },
  ];

  private handVisual(
    slot: number,
    role: 'pitch' | 'volume',
    point: Point2,
    amount: number,
  ): HandVisual {
    const visual = this.handVisualPool[slot] ?? this.handVisualPool[0]!;
    visual.role = role;
    visual.x = point.x;
    visual.y = point.y;
    visual.visible = true;
    visual.amount = amount;
    return visual;
  }

  private updateReadout(control: Readonly<ControlOutput>): void {
    const settings = this.settings.current;
    const sounding = this.audio.running && !settings.muted && control.gain > 0.0015;
    const r = this.readout;
    r.frequency = control.frequency;
    r.note = control.note.name;
    r.cents = control.note.cents;
    r.volume = settings.muted ? 0 : control.volumeNormalized;
    r.pitchNormalized = control.pitchNormalized;
    r.volumeNormalized = control.volumeNormalized;
    r.pitchHandPresent = control.pitchPresent;
    r.volumeHandPresent = control.volumePresent;
    r.handCount = control.handCount;
    r.sounding = sounding;
    r.vibrato = control.vibratoAmount;
    r.vibratoFollowsHand = settings.vibratoMode === 'hand';
    this.ui.setReadout(r);
  }

  private updateDiagnostics(): void {
    if (!this.settings.current.showDiagnostics) return;
    const d = this.diagnostics;
    d.renderFps = this.perf.renderFps;
    d.inferenceFps = this.perf.inferenceFps;
    d.inferenceMs = this.perf.inferenceMs;
    d.handCount = this.controller.state.handCount;
    d.tier = this.tier;
    d.inferenceMode = this.inferenceMode;
    d.targetInferenceHz = this.tracker?.targetHz ?? 0;
    d.audioLatencyMs = this.audio.latencyMs;
    d.droppedFrames = this.perf.droppedFrames;
    this.ui.setDiagnostics(d);
  }

  // ------------------------------------------------------------ environment

  private observeResize(): void {
    if (typeof ResizeObserver !== 'function') {
      globalThis.addEventListener('resize', this.handleResize);
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.ui.surfaces.stage);
  }

  private readonly handleResize = (): void => {
    const stage = this.ui.surfaces.stage;
    const width = stage.clientWidth || globalThis.innerWidth || 1;
    const height = stage.clientHeight || globalThis.innerHeight || 1;
    const dpr = globalThis.devicePixelRatio || 1;
    this.mapper.update({ displayWidth: width, displayHeight: height });
    this.scene?.resize(width, height, dpr);
  };

  private readonly handleVisibility = (): void => {
    if (document.hidden) {
      this.tracker?.pause();
      this.stopRenderLoop();
      void this.audio.suspend();
      this.capabilities.audioRunning = false;
      this.ui.setCapabilities(this.capabilities);
      this.settings.flush();
      return;
    }
    this.tracker?.resume();
    this.perf.reset();
    this.startRenderLoop();
  };

  private readonly handleOrientation = (): void => {
    // Give the browser a frame to settle the new layout before re-measuring.
    requestAnimationFrame(() => this.handleResize());
  };

  private readonly handlePageHide = (): void => {
    this.settings.flush();
    void this.audio.suspend();
  };

  private readonly handleContextLost = (event: Event): void => {
    event.preventDefault();
    this.stopRenderLoop();
    this.machine.fail({
      kind: 'webgl-unavailable',
      message: 'The graphics context was lost. Try again to rebuild the scene.',
      recoverable: true,
    });
  };

  private installGlobalListeners(): void {
    document.addEventListener('visibilitychange', this.handleVisibility);
    globalThis.addEventListener('pagehide', this.handlePageHide);
    globalThis.addEventListener('orientationchange', this.handleOrientation);
    this.ui.surfaces.canvas.addEventListener('webglcontextlost', this.handleContextLost);
  }

  private onStateChanged(state: AppState): void {
    this.ui.surfaces.stage.classList.toggle('is-placing', state === 'placement');
    if (state !== 'placement') this.gestures?.disable();
    if (state === 'noHands' && this.audio.running) {
      // The controller's own fade already handles this; nothing else to do here.
    }
  }

  // -------------------------------------------------------------- test hooks

  get stateName(): AppState {
    return this.machine.current;
  }

  get currentReadout(): Readonly<LiveReadout> {
    return this.readout;
  }

  get settingsStore(): SettingsStore {
    return this.settings;
  }

  get isMockTracking(): boolean {
    return this.mockTracking;
  }

  forceState(state: AppState): void {
    this.machine.transition(state);
  }

  stateCopy(state: AppState): { title: string; body: string } {
    return STATE_COPY[state];
  }

  // ----------------------------------------------------------------- teardown

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.stopRenderLoop();
    document.removeEventListener('visibilitychange', this.handleVisibility);
    globalThis.removeEventListener('pagehide', this.handlePageHide);
    globalThis.removeEventListener('orientationchange', this.handleOrientation);
    globalThis.removeEventListener('resize', this.handleResize);
    this.ui.surfaces.canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mediaQueryCleanup?.();
    this.gestures?.dispose();
    await this.xr?.end();
    this.tracker?.dispose();
    this.camera?.dispose();
    this.scene?.dispose();
    await this.audio.dispose();
    this.settings.flush();
    this.settings.dispose();
    this.machine.dispose();
    this.ui.dispose();
  }
}
